import type { SiteThemeConfig, UnitType } from '@/types/database';

export interface SiteThemePreset {
  id: string;
  name: string;
  description: string;
  // Which unit type(s) this preset is the dedicated, on-brand default for
  // — omitted for the generic presets available to every program. See
  // presetsForUnitType() below.
  unitTypes?: UnitType[];
  config: SiteThemeConfig;
}

const COMFORTABLE_FORMS: SiteThemeConfig['forms'] = {
  fieldShape: 'rounded',
  fieldFill: true,
  border: 'none',
  padding: 'md',
};

const COMFORTABLE_SURFACES: SiteThemeConfig['surfaces'] = { density: 'comfortable' };

function buttons(shape: SiteThemeConfig['buttons']['shape']): SiteThemeConfig['buttons'] {
  return {
    shape,
    primary: { variant: 'contained' },
    secondary: { variant: 'outlined' },
    tertiary: { variant: 'text' },
  };
}

// ============================================================
// Program-dedicated presets — real Scouting America brand colors (see
// BSA-Brand-Colors-and-Fonts-033124.pdf), not approximations. White
// (#ffffff) and Black (#000000) are both official primary colors, used
// consistently as lightest/darkest across every program so every preset
// starts from a guaranteed-max-contrast base; the program identity lives
// in the light/accent/dark slots. Font/shape defaults follow
// scouting-website-template-design-system.md's per-program
// recommendations.
// ============================================================

const cubScoutPack: SiteThemePreset = {
  id: 'cub-scout-pack',
  name: 'Cub Scout Blue',
  description: 'Official Cub Scout Pack colors — friendly, energetic, family-oriented.',
  unitTypes: ['cub_scout_pack'],
  config: {
    palette: { lightest: '#ffffff', light: '#e9e9e4', accent: '#003f87', dark: '#003366', darkest: '#000000' },
    fonts: { headingFont: 'poppins', paragraphFont: 'open-sans', buttonFont: 'inherit-body', miscFont: 'open-sans', baseSize: 17 },
    buttons: buttons('rounded'),
    forms: COMFORTABLE_FORMS,
    surfaces: COMFORTABLE_SURFACES,
  },
};

const scoutsBsaTroop: SiteThemePreset = {
  id: 'scouts-bsa-troop',
  name: 'Scouts BSA Red',
  description: 'Official Scouts BSA colors — structured, capable, outdoor-oriented.',
  unitTypes: ['scouts_bsa_troop'],
  config: {
    palette: { lightest: '#ffffff', light: '#d6cebd', accent: '#ce1126', dark: '#243e2c', darkest: '#000000' },
    fonts: { headingFont: 'georgia', paragraphFont: 'roboto', buttonFont: 'inherit-body', miscFont: 'roboto', baseSize: 17 },
    buttons: buttons('soft'),
    forms: { ...COMFORTABLE_FORMS, fieldShape: 'soft' },
    surfaces: COMFORTABLE_SURFACES,
  },
};

const venturingCrew: SiteThemePreset = {
  id: 'venturing-crew',
  name: 'Venturing Green',
  description: 'Official Venturing colors — contemporary, youth-led, high-energy.',
  unitTypes: ['venturing_crew'],
  config: {
    palette: { lightest: '#ffffff', light: '#e9e9e4', accent: '#006b3f', dark: '#232528', darkest: '#000000' },
    fonts: { headingFont: 'poppins', paragraphFont: 'inter', buttonFont: 'inherit-body', miscFont: 'inter', baseSize: 16 },
    buttons: buttons('pill'),
    forms: COMFORTABLE_FORMS,
    surfaces: COMFORTABLE_SURFACES,
  },
};

const seaScoutShip: SiteThemePreset = {
  id: 'sea-scout-ship',
  name: 'Sea Scout Blue',
  description: 'Official Sea Scout colors — maritime, technical, disciplined.',
  unitTypes: ['sea_scout_ship'],
  config: {
    palette: { lightest: '#ffffff', light: '#9ab3d5', accent: '#003366', dark: '#515354', darkest: '#000000' },
    fonts: { headingFont: 'marcellus', paragraphFont: 'inter', buttonFont: 'inherit-body', miscFont: 'inter', baseSize: 17 },
    buttons: buttons('soft'),
    forms: { ...COMFORTABLE_FORMS, fieldShape: 'soft' },
    surfaces: COMFORTABLE_SURFACES,
  },
};

// ============================================================
// Generic presets — available to every unit type, and the only presets
// offered to unit types this brand sheet doesn't cover (Exploring,
// Girl Scouts, Trail Life, American Heritage Girls). Not badged as
// "official" anything, deliberately — no brand data was provided for
// those organizations, so nothing here claims to be.
// ============================================================

const classic: SiteThemePreset = {
  id: 'classic',
  name: 'Classic',
  description: 'Traditional and established.',
  config: {
    palette: { lightest: '#ffffff', light: '#e7ecf5', accent: '#1f4b99', dark: '#14315f', darkest: '#0a1a33' },
    fonts: { headingFont: 'system', paragraphFont: 'system', buttonFont: 'inherit-body', miscFont: 'system', baseSize: 16 },
    buttons: buttons('rounded'),
    forms: { ...COMFORTABLE_FORMS, padding: 'sm' },
    surfaces: COMFORTABLE_SURFACES,
  },
};

const boldAdventure: SiteThemePreset = {
  id: 'bold-adventure',
  name: 'Bold Adventure',
  description: 'Outdoor-heavy and energetic.',
  config: {
    palette: { lightest: '#fff7ed', light: '#fed7aa', accent: '#c2410c', dark: '#0f766e', darkest: '#0c4a44' },
    fonts: { headingFont: 'poppins', paragraphFont: 'inter', buttonFont: 'inherit-heading', miscFont: 'inter', baseSize: 16 },
    buttons: buttons('pill'),
    forms: { ...COMFORTABLE_FORMS, padding: 'sm' },
    surfaces: COMFORTABLE_SURFACES,
  },
};

const naturalTrail: SiteThemePreset = {
  id: 'natural-trail',
  name: 'Natural Trail',
  description: 'Camp, conservation, and outdoors focus.',
  config: {
    palette: { lightest: '#ffffff', light: '#e8e2d8', accent: '#c98866', dark: '#4a5a58', darkest: '#22292b' },
    fonts: { headingFont: 'marcellus', paragraphFont: 'open-sans', buttonFont: 'inherit-body', miscFont: 'open-sans', baseSize: 16 },
    buttons: buttons('rounded'),
    forms: { ...COMFORTABLE_FORMS, padding: 'sm' },
    surfaces: COMFORTABLE_SURFACES,
  },
};

const modern: SiteThemePreset = {
  id: 'modern-grotesque',
  name: 'Modern',
  description: 'Clean, urban, contemporary.',
  config: {
    palette: { lightest: '#ffffff', light: '#e5e5e5', accent: '#111827', dark: '#374151', darkest: '#030712' },
    fonts: { headingFont: 'inter', paragraphFont: 'inter', buttonFont: 'inherit-body', miscFont: 'inter', baseSize: 16 },
    buttons: buttons('square'),
    forms: { fieldShape: 'square', fieldFill: false, border: 'outline', padding: 'md' },
    surfaces: COMFORTABLE_SURFACES,
  },
};

// The "Themes" gallery order: dedicated program presets first (only ever
// shown when they're relevant — see presetsForUnitType), then the 4
// generic directions.
export const SITE_THEME_PRESETS: SiteThemePreset[] = [
  cubScoutPack,
  scoutsBsaTroop,
  venturingCrew,
  seaScoutShip,
  classic,
  boldAdventure,
  naturalTrail,
  modern,
];

// Program preset first (if one exists for this unit type), then every
// generic preset — matches scouting-website-template-design-system.md's
// 3-layer model (program sets the default; a unit can still pick any
// visual direction on top of it).
export function presetsForUnitType(unitType: UnitType | null | undefined): SiteThemePreset[] {
  const generic = SITE_THEME_PRESETS.filter((preset) => !preset.unitTypes);
  if (!unitType) return generic;
  const dedicated = SITE_THEME_PRESETS.filter((preset) => preset.unitTypes?.includes(unitType));
  return [...dedicated, ...generic];
}

// Every site starts on "Natural Trail" — matches this column's own
// default in supabase/migrations/20260716100000_site_theme_config_v2.sql;
// keep the two in sync if either changes. Deliberately generic (not
// program-specific) since this applies before a unit's own program type
// is necessarily known.
export const DEFAULT_THEME_CONFIG: SiteThemeConfig = naturalTrail.config;
